import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { schoolAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const getSchools = createAsyncThunk('school/getAll', async (_, { rejectWithValue }) => {
  try {
    const response = await schoolAPI.getSchools();
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch schools'));
  }
});

export const getSchool = createAsyncThunk('school/getOne', async (id, { rejectWithValue }) => {
  try {
    const response = await schoolAPI.getSchool(id);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch school'));
  }
});

export const updateSchool = createAsyncThunk('school/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await schoolAPI.updateSchool(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update school'));
  }
});

export const getConfig = createAsyncThunk('school/getConfig', async (id, { rejectWithValue }) => {
  try {
    const response = await schoolAPI.getConfig(id);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch config'));
  }
});

export const updateConfig = createAsyncThunk('school/updateConfig', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await schoolAPI.updateConfig(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update config'));
  }
});

const initialState = {
  schools: [],
  currentSchool: null,
  config: null,
  loading: false,
  error: null,
};

const schoolSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    clearSchoolError: (state) => {
      state.error = null;
    },
    setCurrentSchool: (state, action) => {
      state.currentSchool = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSchools.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSchools.fulfilled, (state, action) => {
        state.loading = false;
        state.schools = action.payload;
      })
      .addCase(getSchools.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSchool.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSchool.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSchool = action.payload;
      })
      .addCase(getSchool.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSchool.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSchool.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSchool = action.payload;
        state.schools = state.schools.map((s) =>
          s.id === action.payload.id ? action.payload : s
        );
      })
      .addCase(updateSchool.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      });
  },
});

export const { clearSchoolError, setCurrentSchool } = schoolSlice.actions;
export default schoolSlice.reducer;
